import { Stack, Table, Text, Card, SimpleGrid } from '@mantine/core';
import type { RateCardRates, AudienceType, OneOnOneSdaType } from '../../../../types';
import { AUDIENCE_TYPE_LABELS, ONE_ON_ONE_SDA_LABELS, ONE_ON_ONE_SDA_TYPES } from '../../../../types';

interface RateCardDisplayProps {
  rates: RateCardRates;
}

const AUDIENCE_TYPES: AudienceType[] = ['emerging', 'established', 'executive'];

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString()}`;
}

function formatCentsUnit(cents: number, unit?: string): string {
  return `$${(cents / 100).toLocaleString()}${unit || ''}`;
}

/** Tiered rate table (audience type rows x duration columns) */
function TieredTable({
  title,
  rates,
  durations,
  durationLabel,
}: {
  title: string;
  rates: Record<string, Record<string, number>>;
  durations: string[];
  durationLabel: (d: string) => string;
}) {
  return (
    <Stack gap="xs">
      <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.3px' }}>
        {title}
      </Text>
      <Table verticalSpacing="sm" horizontalSpacing="md" withRowBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: '40%' }}>
              <Text size="xs" fw={600} tt="uppercase" c="dimmed">
                Audience Type
              </Text>
            </Table.Th>
            {durations.map((d) => (
              <Table.Th key={d} style={{ textAlign: 'right' }}>
                <Text size="xs" fw={600} tt="uppercase" c="dimmed">
                  {durationLabel(d)}
                </Text>
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {AUDIENCE_TYPES.map((tier) => (
            <Table.Tr key={tier}>
              <Table.Td>
                <Text fw={500}>{AUDIENCE_TYPE_LABELS[tier]}</Text>
              </Table.Td>
              {durations.map((d) => (
                <Table.Td key={d} style={{ textAlign: 'right' }}>
                  <Text>{formatCents(rates[tier]?.[d] ?? 0)}</Text>
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}

const FLAT_RATE_CONFIG: { key: string; label: string; unit?: string }[] = [
  { key: 'huddle', label: 'Huddle', unit: '/event' },
  { key: 'immersion', label: 'Immersion', unit: '/event' },
  // MED-910 / E13-8: Cohort Events split into independent Orientation + Wrap-Up.
  { key: 'orientation', label: 'Orientation', unit: '/hr' },
  { key: 'wrap_up', label: 'Wrap-Up', unit: '/hr' },
  { key: 'coach_debrief', label: 'Coach Debrief' },
  { key: 'workshop', label: 'Workshop' },
  { key: 'in_person_half_day', label: 'In-Person Half Day' },
  { key: 'in_person_full_day', label: 'In-Person Full Day' },
];

export function RateCardDisplay({ rates }: RateCardDisplayProps) {
  return (
    <Stack gap="xl">
      {/* Group Session SDA */}
      <TieredTable
        title="Group Session SDA"
        rates={rates.group_session}
        durations={['60', '75', '90']}
        durationLabel={(d) => `${d} min`}
      />

      {/* MED-693: 1:1 SDA — five session-type tables, each independently editable */}
      {ONE_ON_ONE_SDA_TYPES.map((sessionType: OneOnOneSdaType) => (
        <TieredTable
          key={sessionType}
          title={ONE_ON_ONE_SDA_LABELS[sessionType]}
          rates={rates.one_on_one_sda?.[sessionType] ?? {}}
          durations={['45', '60']}
          durationLabel={(d) => `${d} min`}
        />
      ))}

      {/* Per-Event & Flat Rates */}
      <Stack gap="xs">
        <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.3px' }}>
          Per-Event & Flat Rates
        </Text>
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
          {FLAT_RATE_CONFIG.map(({ key, label, unit }) => (
            <Card key={key} withBorder p="sm" radius="sm">
              <Text size="xs" c="dimmed">
                {label}
              </Text>
              <Text fw={700} size="lg">
                {formatCentsUnit(
                  (rates.flat as Record<string, number>)[key] ?? 0,
                  unit,
                )}
              </Text>
            </Card>
          ))}
        </SimpleGrid>
      </Stack>
    </Stack>
  );
}
